import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { aiApi } from '../api';
import CandleChart from '../components/CandleChart';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Legend,
} from 'recharts';

const card = { background: '#1e293b', borderRadius: 12, padding: 24, marginBottom: 20 };

const SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT'];
const INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'];
const DATE_PRESETS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m', days: 180 },
  { label: '1a', days: 365 },
];

const INTERVAL_COLORS = {
  '1m': '#e879f9', '5m': '#34d399', '15m': '#38bdf8',
  '1h': '#a78bfa', '4h': '#fbbf24', '1d': '#f97316',
};

function PnlBadge({ value }) {
  const color = value >= 0 ? '#34d399' : '#f87171';
  return (
    <span style={{ color, fontWeight: 700 }}>
      {value >= 0 ? '+' : ''}{value?.toFixed(2)}%
    </span>
  );
}

function StatBox({ label, value, color = '#e2e8f0', sub }) {
  return (
    <div style={{ background: '#0f172a', borderRadius: 10, padding: '14px 16px', flex: 1, minWidth: 100 }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Utility: format date as YYYY-MM-DD for input[type=date]
function toDateInput(d) {
  return d.toISOString().slice(0, 10);
}

const MTF_TIMEFRAMES = ['1m', '5m', '15m', '1h', '4h', '1d'];
const TF_COLORS = { '1m': '#e879f9', '5m': '#34d399', '15m': '#38bdf8', '1h': '#a78bfa', '4h': '#fbbf24', '1d': '#f97316' };

// Calendar view: group trades by day and show monthly heatmap
// tradeArrays: single array or array of arrays (for combined multi-source view)
function TradeCalendar({ trades, tradeArrays, color = '#38bdf8' }) {
  const allTrades = tradeArrays
    ? tradeArrays.flat()
    : (trades || []);
  const closed = allTrades.filter(t => t.direction === 'close' && t.closed_at);
  if (!closed.length) return null;

  // Group PnL by day with 2 decimal precision
  const byDay = {};
  closed.forEach(t => {
    const day = t.closed_at.slice(0, 10);
    byDay[day] = Math.round(((byDay[day] || 0) + (t.pnl || 0)) * 100) / 100;
  });

  // Find range of months
  const days = Object.keys(byDay).sort();
  const months = [...new Set(days.map(d => d.slice(0, 7)))];
  const maxAbs = Math.max(...Object.values(byDay).map(Math.abs), 1);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, marginBottom: 8 }}>
        Calendário de Resultados
      </div>
      {months.slice(-3).map(month => {
        const [yr, mo] = month.split('-').map(Number);
        const firstDay = new Date(yr, mo - 1, 1).getDay(); // 0=Sun
        const daysInMonth = new Date(yr, mo, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) {
          const key = `${month}-${String(d).padStart(2, '0')}`;
          cells.push({ day: d, pnl: byDay[key] ?? null });
        }
        const monthNames = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
        return (
          <div key={month} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>
              {monthNames[mo - 1]} {yr}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {['D','S','T','Q','Q','S','S'].map((d, i) => (
                <div key={i} style={{ fontSize: 9, color: '#475569', textAlign: 'center', paddingBottom: 2 }}>{d}</div>
              ))}
              {cells.map((cell, i) => {
                if (!cell) return <div key={i} />;
                const { day, pnl } = cell;
                const hasTrade = pnl !== null;
                const intensity = hasTrade ? Math.min(Math.abs(pnl) / maxAbs, 1) : 0;
                const bg = !hasTrade ? '#1e293b'
                  : pnl > 0 ? `rgba(52,211,153,${0.15 + intensity * 0.7})`
                  : `rgba(248,113,113,${0.15 + intensity * 0.7})`;
                return (
                  <div key={i} title={hasTrade ? `${pnl >= 0 ? '+' : ''}$${pnl?.toFixed(2)}` : ''} style={{
                    background: bg, borderRadius: 4, padding: '4px 2px', textAlign: 'center',
                    border: hasTrade ? `1px solid ${pnl > 0 ? '#34d39933' : '#f8717133'}` : '1px solid #1e293b',
                    cursor: hasTrade ? 'default' : 'default',
                  }}>
                    <div style={{ fontSize: 9, color: '#94a3b8' }}>{day}</div>
                    {hasTrade && (
                      <div style={{ fontSize: 7.5, color: pnl > 0 ? '#34d399' : '#f87171', fontWeight: 700, lineHeight: 1.2 }}>
                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Backtest() {
  const [mode, setMode] = useState('arena');   // 'arena' | 'random' | 'multitf'
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [interval, setInterval] = useState('1h');
  const [useTa, setUseTa] = useState(false);
  const [capital, setCapital] = useState(5000);
  const [randomCount, setRandomCount] = useState(20);
  const [randomSeed, setRandomSeed] = useState('');
  const [visibleBots, setVisibleBots] = useState(null);
  const [expandedBot, setExpandedBot] = useState(null);
  const [chartBotFilter, setChartBotFilter] = useState(null);
  const [sortCol, setSortCol] = useState('pnl_pct');
  const [sortDir, setSortDir] = useState('desc');
  const [mtfTimeframes, setMtfTimeframes] = useState(['5m', '15m', '1h', '4h']);

  // chartMarkersFormatted is declared after `result` below — see line ~145
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const [explorationMode, setExplorationMode] = useState(false);

  // Fluxo guiado (stepper): 1 Configuração → 2 Execução → 3 Resultados
  const [step, setStep] = useState(1);
  const [learnFlow, setLearnFlow] = useState(false); // cartão "Aprendizado" = arena + exploração

  // Replay state
  const [playbackIdx, setPlaybackIdx] = useState(null); // null = show full result
  const [isPlaying, setIsPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(2);   // candles per frame (frame = 80ms)
  const [liveFeed, setLiveFeed] = useState([]);         // recent trade events during replay
  const replayRef = useRef(null);

  // Date range state (default: last 90 days)
  const defaultEnd = new Date();
  const defaultStart = new Date(defaultEnd);
  defaultStart.setDate(defaultStart.getDate() - 90);
  const [startDate, setStartDate] = useState(toDateInput(defaultStart));
  const [endDate, setEndDate] = useState(toDateInput(defaultEnd));

  const { data: cacheInfo, refetch: refetchCache } = useQuery({
    queryKey: ['cache', symbol, interval],
    queryFn: () => aiApi.get(`/cache/${symbol}?interval=${interval}`).then(r => r.data),
  });

  const { data: dlStatus, refetch: refetchDlStatus } = useQuery({
    queryKey: ['download-status'],
    queryFn: () => aiApi.get('/cache/download-status').then(r => r.data),
    refetchInterval: showDownloadPanel ? 2000 : false,
  });

  const downloadAll = useMutation({
    mutationFn: () => aiApi.post(`/cache/download-all?symbols=${symbol},ETHUSDT`).then(r => r.data),
    onSuccess: () => { setShowDownloadPanel(true); refetchDlStatus(); },
  });

  const download1mMax = useMutation({
    mutationFn: () => aiApi.post(`/cache/download-1m-max?symbols=${symbol},ETHUSDT&days=1095`).then(r => r.data),
    onSuccess: () => { setShowDownloadPanel(true); refetchDlStatus(); },
  });

  const runBacktest = useMutation({
    mutationFn: () => {
      if (mode === 'multitf') {
        return aiApi.post('/arena/backtest/multitf', {
          symbol, timeframes: mtfTimeframes, capital,
          start_date: startDate, end_date: endDate,
          exploration: explorationMode,
        }).then(r => r.data);
      }
      if (mode === 'random') {
        return aiApi.post('/arena/backtest/random', {
          symbol, interval, use_ta: useTa, capital,
          count: randomCount,
          seed: randomSeed ? parseInt(randomSeed) : null,
          start_date: startDate, end_date: endDate,
          exploration: explorationMode,
        }).then(r => r.data);
      }
      return aiApi.post(
        `/arena/backtest?symbol=${symbol}&interval=${interval}&use_ta=${useTa}&capital=${capital}&start_date=${startDate}&end_date=${endDate}&exploration=${explorationMode}`
      ).then(r => r.data);
    },
    onSuccess: () => { setVisibleBots(null); setExpandedBot(null); setChartBotFilter(null); setStep(3); },
  });

  const saveFeedback = useMutation({
    mutationFn: () => aiApi.post(`/arena/backtest/feedback?symbol=${symbol}`).then(r => r.data),
  });

  const learnFromBacktest = useMutation({
    mutationFn: () => aiApi.post(`/arena/backtest/learn?symbol=${symbol}&interval=${interval}`).then(r => r.data),
  });

  const result = runBacktest.data;
  const bots = result?.bots || [];
  const maxLen = bots[0]?.equity_curve?.length || 0;
  const round2 = v => Math.round(v * 100) / 100;

  // Markers for candle chart — only shown when a specific bot is selected.
  // Open: shows trade number + direction (e.g. "#3 ▲")
  // Close: same number + PnL with 2 decimal places (e.g. "#3 +$25.50")
  // Close color: green if win, red if loss (regardless of bot color)
  const chartMarkersFormatted = useMemo(() => {
    if (!result?.chart_markers || !chartBotFilter) return [];
    return result.chart_markers
      .filter(m => m.bot_id === chartBotFilter)
      .map(m => {
        const isOpen = m.direction === 'open';
        const isLong = m.side === 'long';
        const num = m.trade_num ? `#${m.trade_num} ` : '';
        const label = isOpen
          ? `${num}${isLong ? '▲' : '▼'}`
          : m.pnl != null
            ? `${num}${m.pnl >= 0 ? '+' : ''}$${Math.abs(m.pnl).toFixed(2)}`
            : `${num}●`;
        return {
          time: m.time,
          signal: isOpen ? (isLong ? 'buy' : 'sell') : 'hold',
          confidence: 1,
          _color: m.color,   // already win/loss colored in backend
          _label: label,
        };
      });
  }, [result?.chart_markers, chartBotFilter]);

  // Reset replay when new result arrives
  useEffect(() => {
    if (result) {
      setPlaybackIdx(null);
      setIsPlaying(false);
      setLiveFeed([]);
      if (replayRef.current) clearInterval(replayRef.current);
    }
  }, [result]);

  // Collect all trade events sorted by timestamp
  const allTradeEvents = bots.flatMap(b =>
    (b.recent_trades || [])
      .filter(t => t.direction === 'close' && t.closed_at)
      .map(t => ({
        botId: b.id, botName: b.name, botEmoji: b.emoji, botColor: b.color,
        side: t.side, pnl: t.pnl, pnl_pct: t.pnl_pct, reason: t.reason,
        ts: new Date(t.closed_at).getTime() / 1000,
      }))
  ).sort((a, b) => a.ts - b.ts);

  const startReplay = useCallback(() => {
    if (!result || maxLen === 0) return;
    setPlaybackIdx(0);
    setIsPlaying(true);
    setLiveFeed([]);
  }, [result, maxLen]);

  const pauseReplay = useCallback(() => {
    setIsPlaying(false);
    if (replayRef.current) clearInterval(replayRef.current);
  }, []);

  const showFull = useCallback(() => {
    pauseReplay();
    setPlaybackIdx(null);
  }, [pauseReplay]);

  // Playback ticker
  useEffect(() => {
    if (replayRef.current) clearInterval(replayRef.current);
    if (!isPlaying || playbackIdx === null) return;
    replayRef.current = setInterval(() => {
      setPlaybackIdx(prev => {
        const next = (prev || 0) + replaySpeed;
        if (next >= maxLen) {
          setIsPlaying(false);
          setPlaybackIdx(null); // snap to full on finish
          return null;
        }
        return next;
      });
    }, 80);
    return () => clearInterval(replayRef.current);
  }, [isPlaying, replaySpeed, maxLen]);

  // Detect new trades entering the replay window
  useEffect(() => {
    if (playbackIdx === null || bots.length === 0) return;
    const currentTime = bots[0]?.equity_curve?.[playbackIdx - 1]?.time;
    if (!currentTime) return;
    const newEvents = allTradeEvents.filter(e =>
      e.ts <= currentTime &&
      e.ts > (bots[0]?.equity_curve?.[Math.max(0, playbackIdx - replaySpeed - 1)]?.time || 0)
    );
    if (newEvents.length > 0) {
      setLiveFeed(prev => [...newEvents, ...prev].slice(0, 8));
    }
  }, [playbackIdx]);

  // Display index — null means show full result
  const displayEnd = playbackIdx !== null ? playbackIdx : maxLen;
  const currentReplayTime = playbackIdx !== null
    ? bots[0]?.equity_curve?.[Math.min(playbackIdx, maxLen - 1)]?.time
    : null;

  // For bot cards: equity at current replay position
  const botEquityAt = (bot, idx) => {
    if (!bot.equity_curve || bot.equity_curve.length === 0) return bot.initial_capital || 5000;
    const i = Math.min(idx === null ? bot.equity_curve.length - 1 : idx, bot.equity_curve.length - 1);
    return bot.equity_curve[Math.max(0, i)]?.value ?? bot.initial_capital;
  };

  // Merge all equity curves for the combined chart
  const allTimes = bots.length > 0
    ? bots[0].equity_curve.map(p => p.time)
    : [];

  // Subsample for chart performance (max 600 points regardless of source size)
  const chartData = (() => {
    const slice = allTimes.slice(0, displayEnd);
    const step = Math.max(1, Math.floor(slice.length / 600));
    return slice
      .filter((_, i) => i % step === 0 || i === slice.length - 1)
      .map((t, si) => {
        const origI = si * step;
        const point = { time: t };
        bots.forEach(b => {
          if (visibleBots === null || visibleBots.has(b.id)) {
            point[b.id] = b.equity_curve[Math.min(origI, displayEnd - 1)]?.value;
          }
        });
        return point;
      });
  })();

  const toggleBot = (id) => {
    if (visibleBots === null) {
      setVisibleBots(new Set(bots.map(b => b.id).filter(bid => bid !== id)));
    } else {
      const next = new Set(visibleBots);
      if (next.has(id)) {
        if (next.size > 1) next.delete(id);
      } else {
        next.add(id);
        if (next.size === bots.length) setVisibleBots(null);
      }
      setVisibleBots(next);
    }
  };

  const formatTime = (t) => {
    if (!t) return '';
    const d = new Date(t * 1000);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Backtest Arena</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>
        Replay de histórico em alta velocidade — todos os bots operam simultaneamente contra os mesmos dados.
      </p>

      {/* ── Stepper horizontal: 1 Configuração → 2 Execução → 3 Resultados ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { n: 1, label: 'Configuração' },
          { n: 2, label: 'Execução' },
          { n: 3, label: 'Resultados' },
        ].map((s, i) => {
          const active = step === s.n;
          const done = step > s.n;
          const reachable = s.n === 1 || (s.n === 2 && mode) || (s.n === 3 && (result || runBacktest.isPending));
          return (
            <React.Fragment key={s.n}>
              {i > 0 && (
                <div style={{ width: 40, height: 2, background: step > s.n ? '#38bdf8' : '#334155', margin: '0 4px' }} />
              )}
              <button
                onClick={() => { if (reachable) setStep(s.n); }}
                disabled={!reachable}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: active ? '#1e3a5f' : '#1e293b',
                  border: `2px solid ${active ? '#38bdf8' : done ? '#34d39955' : '#334155'}`,
                  borderRadius: 10, padding: '8px 16px', cursor: reachable ? 'pointer' : 'default',
                  opacity: active || done || reachable ? 1 : 0.5,
                }}
              >
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: active ? '#38bdf8' : done ? '#34d399' : '#334155',
                  color: active || done ? '#0f172a' : '#64748b', fontSize: 12, fontWeight: 800,
                }}>
                  {done ? '✓' : s.n}
                </span>
                <span style={{ color: active ? '#38bdf8' : done ? '#94a3b8' : '#64748b', fontWeight: 700, fontSize: 13 }}>
                  {s.label}
                </span>
              </button>
            </React.Fragment>
          );
        })}
        {result && step !== 3 && (
          <button onClick={() => setStep(3)} style={{
            marginLeft: 'auto', background: '#0f172a', color: '#38bdf8',
            border: '1px solid #38bdf8', borderRadius: 8, padding: '6px 14px',
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}>
            📊 Ver resultados da última execução
          </button>
        )}
      </div>

      {/* ── ETAPA 1: Configuração ── */}
      {step === 1 && (
      <>
      {/* Mode selector (dentro da etapa 1) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
        {[
          { id: 'arena',   label: '🏆 Arena (7 bots fixos)',    desc: 'Estratégias pré-definidas' },
          { id: 'random',  label: '🎲 Busca Aleatória',         desc: 'Gera N setups aleatórios' },
          { id: 'multitf', label: '🌐 Bot Multi-Gráfico',       desc: 'Opera em múltiplos TFs com confirmação hierárquica' },
          { id: 'learn',   label: '🎓 Aprendizado',             desc: 'Arena com 🔭 Exploração — gera exemplos para o modelo aprender' },
        ].map(m => {
          const selected = m.id === 'learn' ? (mode === 'arena' && learnFlow) : (mode === m.id && !learnFlow);
          return (
          <button key={m.id} onClick={() => {
            if (m.id === 'learn') { setMode('arena'); setLearnFlow(true); setExplorationMode(true); }
            else { setMode(m.id); setLearnFlow(false); }
          }} style={{
            background: selected ? '#1e3a5f' : '#1e293b',
            color: selected ? '#38bdf8' : '#64748b',
            border: `2px solid ${selected ? '#38bdf8' : '#334155'}`,
            borderRadius: 10, padding: '10px 20px', cursor: 'pointer', textAlign: 'left',
          }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{m.label}</div>
            <div style={{ fontSize: 11, marginTop: 2 }}>{m.desc}</div>
          </button>
          );
        })}
      </div>

      {/* Config panel */}
      <div style={card}>
        <h2 style={{ fontSize: 14, color: '#94a3b8', marginBottom: 16 }}>Configuração</h2>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>

          {/* Symbol */}
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Ativo</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {SYMBOLS.map(s => (
                <button key={s} onClick={() => setSymbol(s)} style={{
                  background: symbol === s ? '#38bdf8' : '#0f172a',
                  color: symbol === s ? '#0f172a' : '#94a3b8',
                  border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 12,
                }}>{s.replace('USDT', '')}</button>
              ))}
            </div>
          </div>

          {/* Interval (não usado pelo modo multi-timeframe) */}
          {!(mode === 'multitf') && (
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Timeframe</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {INTERVALS.map(iv => (
                <button key={iv} onClick={() => setInterval(iv)} style={{
                  background: interval === iv ? (INTERVAL_COLORS[iv] + '44') : '#0f172a',
                  color: interval === iv ? INTERVAL_COLORS[iv] : '#64748b',
                  border: `1px solid ${interval === iv ? INTERVAL_COLORS[iv] : '#334155'}`,
                  borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12,
                }}>{iv}</button>
              ))}
            </div>
          </div>
          )}

          {/* Date range */}
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Período de teste</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '5px 8px', fontSize: 12 }} />
              <span style={{ color: '#64748b', fontSize: 12 }}>→</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '5px 8px', fontSize: 12 }} />
              <span style={{ color: '#475569', fontSize: 11 }}>atalhos:</span>
              {DATE_PRESETS.map(p => (
                <button key={p.days} onClick={() => {
                  const end = new Date();
                  const start = new Date(end);
                  start.setDate(start.getDate() - p.days);
                  setStartDate(toDateInput(start));
                  setEndDate(toDateInput(end));
                }} style={{
                  background: '#0f172a', color: '#64748b', border: '1px solid #334155',
                  borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11,
                }}>{p.label}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
              Para teste válido (out-of-sample): use dados que o modelo nunca viu no treino
            </div>
          </div>

          {/* Capital inicial */}
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Capital inicial por bot</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[1000, 5000, 10000, 50000].map(c => (
                <button key={c} onClick={() => setCapital(c)} style={{
                  background: capital === c ? '#334155' : '#0f172a',
                  color: capital === c ? '#e2e8f0' : '#64748b',
                  border: '1px solid #334155', borderRadius: 6,
                  padding: '5px 10px', cursor: 'pointer', fontSize: 12,
                }}>${c.toLocaleString()}</button>
              ))}
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: 12 }}>$</span>
                <input
                  type="number"
                  value={capital}
                  onChange={e => setCapital(Math.max(100, Number(e.target.value)))}
                  style={{
                    background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155',
                    borderRadius: 6, padding: '5px 8px 5px 18px', fontSize: 12, width: 90,
                  }}
                  min={100}
                />
              </div>
            </div>
          </div>

          {/* Multi-TF options */}
          {mode === 'multitf' && (
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Timeframes ativos</div>
              <div style={{ fontSize: 11, color: '#475569', marginBottom: 8 }}>
                Capital total dividido igualmente. Cada TF confirma o anterior (maior bloqueia menor se oposto).
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {MTF_TIMEFRAMES.map(tf => {
                  const active = mtfTimeframes.includes(tf);
                  return (
                    <button key={tf} onClick={() => {
                      if (active && mtfTimeframes.length > 1)
                        setMtfTimeframes(mtfTimeframes.filter(t => t !== tf));
                      else if (!active)
                        setMtfTimeframes([...mtfTimeframes, tf].sort((a, b) => MTF_TIMEFRAMES.indexOf(a) - MTF_TIMEFRAMES.indexOf(b)));
                    }} style={{
                      background: active ? `${TF_COLORS[tf]}22` : '#0f172a',
                      color: active ? TF_COLORS[tf] : '#64748b',
                      border: `2px solid ${active ? TF_COLORS[tf] : '#334155'}`,
                      borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                    }}>{tf}</button>
                  );
                })}
              </div>
              {mtfTimeframes.length > 0 && (
                <div style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>
                  Capital por TF: <b style={{ color: '#e2e8f0' }}>${Math.round(capital / mtfTimeframes.length).toLocaleString()}</b>
                  {' · '}{mtfTimeframes.map((tf, i) => (
                    <span key={tf} style={{ color: TF_COLORS[tf] }}>
                      {tf}{i < mtfTimeframes.length - 1 ? ' → ' : ''}
                    </span>
                  ))}
                  {' '}(maior confirma menor)
                </div>
              )}
            </div>
          )}

          {/* Random search options */}
          {mode === 'random' && (
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Setups aleatórios</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {[1, 5, 10, 20, 50].map(n => (
                  <button key={n} onClick={() => setRandomCount(n)} style={{
                    background: randomCount === n ? '#334155' : '#0f172a',
                    color: randomCount === n ? '#e2e8f0' : '#64748b',
                    border: '1px solid #334155', borderRadius: 6,
                    padding: '5px 10px', cursor: 'pointer', fontSize: 12,
                  }}>{n}</button>
                ))}
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={randomCount}
                  onChange={e => setRandomCount(Math.max(1, Math.min(50, Number(e.target.value))))}
                  style={{
                    background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155',
                    borderRadius: 6, padding: '5px 8px', fontSize: 12, width: 60,
                  }}
                />
                <input
                  type="number"
                  placeholder="Seed (opcional)"
                  value={randomSeed}
                  onChange={e => setRandomSeed(e.target.value)}
                  style={{
                    background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155',
                    borderRadius: 6, padding: '5px 10px', fontSize: 12, width: 120,
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                Mesmo seed = mesmos setups (reprodutível)
              </div>
            </div>
          )}

          {/* Exploration mode — travado em Exploração quando o fluxo é Aprendizado */}
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Modo</div>
            {learnFlow ? (
              <div style={{
                background: '#2d1a5e', color: '#a78bfa',
                border: '1px solid #a78bfa', borderRadius: 6,
                padding: '6px 14px', fontSize: 12, fontWeight: 700, display: 'inline-block',
              }}>
                🔭 Exploração (fixo no fluxo de Aprendizado)
              </div>
            ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setExplorationMode(false)} style={{
                background: !explorationMode ? '#1e3a5f' : '#0f172a',
                color: !explorationMode ? '#38bdf8' : '#64748b',
                border: `1px solid ${!explorationMode ? '#38bdf8' : '#334155'}`,
                borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12,
              }}>
                🎯 Normal
              </button>
              <button onClick={() => setExplorationMode(true)} style={{
                background: explorationMode ? '#2d1a5e' : '#0f172a',
                color: explorationMode ? '#a78bfa' : '#64748b',
                border: `1px solid ${explorationMode ? '#a78bfa' : '#334155'}`,
                borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12,
              }}>
                🔭 Exploração
              </button>
            </div>
            )}
            {(explorationMode || learnFlow) && (
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, maxWidth: 340 }}>
                Opera com qualquer sinal (conf ≥ 51%) — força o bot a explorar o período inteiro. Use para gerar dados de aprendizado. Teste em outro período depois.
              </div>
            )}
          </div>

          {/* TA confirmation toggle (não usado pelo modo multi-timeframe) */}
          {!(mode === 'multitf') && (
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Confirmação TA</div>
            <button onClick={() => setUseTa(!useTa)} style={{
              background: useTa ? '#1e3a1e' : '#0f172a',
              color: useTa ? '#34d399' : '#475569',
              border: `1px solid ${useTa ? '#34d399' : '#334155'}`,
              borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12,
            }}>
              {useTa ? '✓ TA ativo (mais lento)' : '○ Sem TA (mais rápido)'}
            </button>
          </div>
          )}

          {/* Cache status + bulk download */}
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                Cache {symbol} {interval}:{' '}
                {cacheInfo?.cached
                  ? <span style={{ color: '#34d399' }}>✓ {cacheInfo.rows?.toLocaleString()} candles · {cacheInfo.from?.slice(0,10)} → {cacheInfo.to?.slice(0,10)}</span>
                  : <span style={{ color: '#f87171' }}>sem dados</span>}
              </div>
              <button
                onClick={() => { downloadAll.mutate(); setShowDownloadPanel(true); }}
                disabled={downloadAll.isPending}
                style={{
                  background: '#1e3a5f', color: '#38bdf8', border: '1px solid #38bdf8',
                  borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                }}
              >
                {downloadAll.isPending ? '⏳ Iniciando...' : '⬇ Todos os intervalos'}
              </button>
              <button
                onClick={() => { download1mMax.mutate(); setShowDownloadPanel(true); }}
                disabled={download1mMax.isPending || dlStatus?.details?.['BTCUSDT_1m']?.status === 'running'}
                title="Baixa 3 anos de 1m (~4 min) — ideal para backtest de longo prazo"
                style={{
                  background: '#2d1a5e', color: '#a78bfa', border: '1px solid #a78bfa',
                  borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                }}
              >
                {dlStatus?.details?.['BTCUSDT_1m']?.status === 'running'
                  ? '⏳ 1m baixando...'
                  : download1mMax.isPending ? '⏳ Iniciando...'
                  : '🕐 Máximo 1m (3 anos)'}
              </button>
              {dlStatus && (
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  {dlStatus.done}/{dlStatus.total} · {dlStatus.running} rodando
                  <button onClick={() => setShowDownloadPanel(v => !v)}
                    style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11, marginLeft: 6 }}>
                    {showDownloadPanel ? '▲' : '▼'} detalhes
                  </button>
                </span>
              )}
            </div>
            {showDownloadPanel && dlStatus && (
              <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px', fontSize: 11, maxHeight: 200, overflowY: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4 }}>
                  {Object.entries(dlStatus.details || {}).map(([key, info]) => (
                    <div key={key} style={{ marginBottom: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', marginBottom: 2 }}>
                        <span style={{ fontFamily: 'monospace' }}>{key}</span>
                        <span style={{
                          color: info.status === 'done' ? '#34d399'
                            : info.status === 'running' ? '#fbbf24'
                            : info.status === 'error' ? '#f87171' : '#64748b',
                          fontWeight: 600,
                        }}>
                          {info.status === 'done'
                            ? `✓ ${info.rows?.toLocaleString()} candles`
                            : info.status === 'running'
                            ? `${info.rows?.toLocaleString() || 0} / ~${info.rows_target?.toLocaleString() || '?'} (${info.pct || 0}%)`
                            : info.status}
                        </span>
                      </div>
                      {info.status === 'running' && info.pct > 0 && (
                        <div style={{ background: '#1e293b', borderRadius: 3, height: 4 }}>
                          <div style={{ background: '#fbbf24', width: `${info.pct}%`, height: '100%', borderRadius: 3, transition: 'width .5s' }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => { runBacktest.mutate(); setStep(2); }}
              disabled={runBacktest.isPending}
              style={{
                background: runBacktest.isPending ? '#334155' : '#6366f1',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 24px', cursor: runBacktest.isPending ? 'default' : 'pointer', fontWeight: 700, fontSize: 14,
              }}
            >
              {runBacktest.isPending ? '⏳ Simulando...' : '▶ Continuar → Executar'}
            </button>
            {result && (
              <button
                onClick={() => setStep(3)}
                style={{
                  background: '#0f172a', color: '#64748b', border: '1px solid #334155',
                  borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontSize: 13,
                }}
              >
                📊 Ver último resultado
              </button>
            )}
          </div>
        </div>

        {runBacktest.isPending && (
          <div style={{ marginTop: 16, fontSize: 12, color: '#64748b' }}>
            Pré-computando sinais ML e simulando posições candle-a-candle...
          </div>
        )}
        {runBacktest.isError && (
          <div style={{ marginTop: 12, color: '#f87171', fontSize: 12 }}>
            Erro: {runBacktest.error?.response?.data?.detail || runBacktest.error?.message}
          </div>
        )}
      </div>
      </>
      )}

      {/* ── ETAPA 2: Execução ── */}
      {step === 2 && (
        <div style={card}>
          <h2 style={{ fontSize: 14, color: '#94a3b8', marginBottom: 16 }}>Execução em andamento</h2>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
            {learnFlow ? '🎓 Aprendizado (Arena + Exploração)' : mode === 'multitf' ? '🌐 Multi-Gráfico' : mode === 'random' ? '🎲 Busca Aleatória' : '🏆 Arena'}
            {' · '}{symbol} {mode !== 'multitf' ? `· ${interval}` : `· ${mtfTimeframes.join(', ')}`}
            {' · '}{startDate} → {endDate} · capital ${capital.toLocaleString()}
            {explorationMode && ' · 🔭 Exploração'}
          </div>
          {runBacktest.isPending ? (
            <>
              <div style={{ background: '#0f172a', borderRadius: 8, height: 8, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{
                  width: '40%', height: '100%', background: 'linear-gradient(90deg, #38bdf8, #6366f1)',
                  borderRadius: 8,
                  animation: 'backtestSlide 1.2s ease-in-out infinite',
                }} />
              </div>
              <style>{`
                @keyframes backtestSlide { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }
              `}</style>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Pré-computando sinais ML e simulando posições candle-a-candle...
              </div>
            </>
          ) : runBacktest.isError ? (
            <div style={{ color: '#f87171', fontSize: 12 }}>
              Erro: {runBacktest.error?.response?.data?.detail || runBacktest.error?.message}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Clique em "▶ Continuar → Executar" na etapa anterior para iniciar a simulação.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button
              onClick={() => setStep(1)}
              style={{
                background: '#0f172a', color: '#94a3b8', border: '1px solid #334155',
                borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 12,
              }}
            >
              ← Voltar / Ajustar configuração
            </button>
            {(result || runBacktest.isSuccess) && (
              <button
                onClick={() => setStep(3)}
                style={{
                  background: '#0f172a', color: '#34d399', border: '1px solid #34d399',
                  borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 12,
                }}
              >
                ✓ Ver resultados
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── ETAPA 3: Resultados ── */}
      {(step === 3) && (
      <>
      {/* Results */}
      {result && (
        <>
          {/* Data range warning */}
          {result.data_available_from && startDate < result.data_available_from?.slice(0, 10) && (
            <div style={{ ...card, background: '#1c0a0a', border: '1px solid #f87171', padding: '12px 20px' }}>
              <div style={{ color: '#f87171', fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
                ⚠ Dados insuficientes para o período solicitado
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
                Você pediu desde <b style={{ color: '#e2e8f0' }}>{startDate}</b> mas o cache começa em <b style={{ color: '#fbbf24' }}>{result.data_available_from?.slice(0, 10)}</b> para {interval}.<br />
                Clique em <b style={{ color: '#38bdf8' }}>⬇ Baixar histórico completo</b> para baixar mais dados.
              </div>
            </div>
          )}

          {/* 0 trades warning */}
          {result.total_trades_all_bots === 0 && (
            <div style={{ ...card, background: '#1c1208', border: '1px solid #f59e0b', padding: '12px 20px' }}>
              <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
                Nenhum trade executado — veja o diagnóstico em cada bot abaixo
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
                As causas mais comuns são: <b style={{ color: '#e2e8f0' }}>confiança do modelo abaixo do mínimo do bot</b> (modelo não conhece os padrões desse período) ou <b style={{ color: '#e2e8f0' }}>sem dados em cache</b> para o intervalo/período selecionado.<br />
                <b style={{ color: '#4ade80' }}>Solução:</b> clique em <b style={{ color: '#4ade80' }}>🎓 Aprender com este período</b> e rode o backtest novamente.
              </div>
            </div>
          )}

          {/* Data leakage warning — only if this is likely in-sample */}
          {result.in_sample_warning !== false && (
            <div style={{ ...card, background: '#1c1208', border: '1px solid #f59e0b', padding: '12px 18px' }}>
              <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 4, fontSize: 12 }}>
                ⚠ Resultado possivelmente IN-SAMPLE — modelo pode ter visto esses dados no treino
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                Para teste realista: treine com dados até X meses atrás, teste nos X meses seguintes.
              </div>
            </div>
          )}

          {/* Summary header */}
          <div style={{ ...card, borderLeft: '4px solid #6366f1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 13, color: '#6366f1', fontWeight: 700, marginBottom: 4 }}>
                  Resultado · {result.symbol} {result.interval} · {result.days} dias
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {result.period?.from?.slice(0, 10)} → {result.period?.to?.slice(0, 10)} · {result.candles_simulated} candles simulados
                  {result.use_ta && <span style={{ color: '#34d399', marginLeft: 8 }}>· com TA</span>}
                  {result.exploration_mode && <span style={{ color: '#a78bfa', marginLeft: 8, fontWeight: 700 }}>🔭 Modo Exploração (conf ≥ 51%)</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {/* Learn from this period */}
                {result.training_examples > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button
                      onClick={() => learnFromBacktest.mutate()}
                      disabled={learnFromBacktest.isPending || learnFromBacktest.isSuccess}
                      style={{
                        background: learnFromBacktest.isSuccess ? '#052e16' : '#1e3a1e',
                        color: learnFromBacktest.isSuccess ? '#34d399' : '#4ade80',
                        border: `1px solid ${learnFromBacktest.isSuccess ? '#34d399' : '#4ade80'}`,
                        borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
                        fontSize: 12, fontWeight: 700,
                      }}
                    >
                      {learnFromBacktest.isPending ? '🧠 Aprendendo...'
                        : learnFromBacktest.isSuccess
                          ? `✓ Aprendeu ${learnFromBacktest.data?.examples_used?.toLocaleString()} exemplos`
                          : `🎓 Aprender com este período (${result.training_examples?.toLocaleString()} exemplos)`}
                    </button>
                    {learnFromBacktest.isSuccess && (
                      <div style={{ fontSize: 10, color: '#64748b' }}>
                        {learnFromBacktest.data?.message}
                      </div>
                    )}
                  </div>
                )}

                {/* Winning trades feedback */}
                {result.feedback_candidates > 0 && (
                  <button
                    onClick={() => saveFeedback.mutate()}
                    disabled={saveFeedback.isPending || saveFeedback.isSuccess}
                    style={{
                      background: '#422006', color: '#fbbf24',
                      border: '1px solid #f59e0b', borderRadius: 6,
                      padding: '6px 12px', cursor: 'pointer', fontSize: 11,
                    }}
                  >
                    {saveFeedback.isPending ? 'Salvando...'
                      : saveFeedback.isSuccess ? `✓ ${saveFeedback.data?.saved} trades salvos`
                      : `💾 ${result.feedback_candidates} trades vencedores → feedback`}
                  </button>
                )}
              </div>
            </div>

            {/* Leaderboard row */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {bots.map((b, i) => (
                <div
                  key={b.id}
                  onClick={() => toggleBot(b.id)}
                  style={{
                    background: '#0f172a',
                    border: `2px solid ${(visibleBots === null || visibleBots.has(b.id)) ? b.color : '#334155'}`,
                    borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
                    opacity: (visibleBots === null || visibleBots.has(b.id)) ? 1 : 0.4,
                    minWidth: 130,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    {i === 0 && <span>🥇</span>}
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{b.emoji} {b.name}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>
                    {b.interval} · {b.leverage}x{b.allow_short ? ' L+S' : ' Long'}
                  </div>
                  {b.bankrupt ? (
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#f87171' }}>💀 FALIDO</div>
                  ) : (() => {
                    const eq = botEquityAt(b, playbackIdx !== null ? displayEnd - 1 : null);
                    const pct = ((eq - (b.initial_capital || capital)) / (b.initial_capital || capital)) * 100;
                    return (
                      <div style={{ fontSize: 18, fontWeight: 800 }}>
                        <PnlBadge value={pct} />
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    {b.total_trades} trades · {b.win_rate}% win
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Comparison table ── */}
          {bots.length > 1 && (() => {
            const cols = [
              { key: 'name',          label: 'Bot',        fmt: (b) => `${b.emoji} ${b.name}` },
              { key: 'pnl_pct',       label: 'PnL %',      fmt: (b) => <span style={{ color: (b.pnl_pct||0) >= 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>{(b.pnl_pct||0) >= 0 ? '+' : ''}{b.pnl_pct?.toFixed(1)}%</span> },
              { key: 'final_capital', label: 'Capital Final', fmt: (b) => b.bankrupt ? <span style={{ color: '#f87171' }}>💀 FALIDO</span> : `$${b.final_capital?.toLocaleString()}` },
              { key: 'total_trades',  label: 'Trades',     fmt: (b) => b.total_trades },
              { key: 'win_rate',      label: 'Win %',      fmt: (b) => <span style={{ color: (b.win_rate||0) >= 50 ? '#34d399' : '#f87171' }}>{b.win_rate}%</span> },
              { key: 'max_drawdown_pct', label: 'Max DD', fmt: (b) => <span style={{ color: '#f87171' }}>-{b.max_drawdown_pct}%</span> },
            ];
            const th = { fontSize: 10, color: '#64748b', padding: '6px 12px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', borderBottom: '1px solid #334155' };
            const td = { fontSize: 12, padding: '7px 12px', borderBottom: '1px solid #1e293b' };
            const sorted = [...bots].sort((a, b) => {
              const va = a[sortCol] ?? 0, vb = b[sortCol] ?? 0;
              return sortDir === 'asc' ? va - vb : vb - va;
            });
            const toggleSort = (col) => {
              if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
              else { setSortCol(col); setSortDir('desc'); }
            };
            return (
              <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', padding: '14px 18px 0' }}>
                  Comparativo de Bots
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {cols.map(c => (
                          <th key={c.key} onClick={() => c.key !== 'name' && toggleSort(c.key)}
                            style={{ ...th, color: sortCol === c.key ? '#38bdf8' : '#64748b' }}>
                            {c.label} {sortCol === c.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((b, i) => (
                        <tr key={b.id} style={{ background: i % 2 === 0 ? '#0f172a' : 'transparent' }}>
                          {cols.map(c => (
                            <td key={c.key} style={td}>{c.fmt(b)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* ── Candle chart with trade markers (PRIMARY) ── */}
          {result.chart_candles?.length > 0 && (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <h2 style={{ fontSize: 14, color: '#94a3b8', marginBottom: 2 }}>
                    Gráfico de Candles · Entradas e Saídas
                  </h2>
                  <div style={{ fontSize: 11, color: '#475569' }}>
                    {result.chart_candles.length} candles · {result.chart_markers?.length || 0} marcadores · slippage {result.slippage_pct}% por lado
                  </div>
                </div>
                {/* Bot filter for markers */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => setChartBotFilter(null)} style={{
                    background: chartBotFilter === null ? '#334155' : '#0f172a',
                    color: chartBotFilter === null ? '#e2e8f0' : '#64748b',
                    border: '1px solid #334155', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                  }}>Todos</button>
                  {bots.map(b => (
                    <button key={b.id} onClick={() => setChartBotFilter(chartBotFilter === b.id ? null : b.id)} style={{
                      background: chartBotFilter === b.id ? `${b.color}33` : '#0f172a',
                      color: chartBotFilter === b.id ? b.color : '#64748b',
                      border: `1px solid ${chartBotFilter === b.id ? b.color : '#334155'}`,
                      borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                    }}>{b.emoji} {b.name}</button>
                  ))}
                </div>
              </div>

              {/* Legend — only when bot is selected */}
              {chartBotFilter && (
                <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 11, color: '#64748b', flexWrap: 'wrap', alignItems: 'center' }}>
                  {(() => {
                    const b = bots.find(x => x.id === chartBotFilter);
                    return b ? <span style={{ color: b.color, fontWeight: 700 }}>{b.emoji} {b.name}</span> : null;
                  })()}
                  <span>▲ Entrada Long</span>
                  <span>▼ Entrada Short</span>
                  <span style={{ color: '#94a3b8' }}>● Fechamento (com PnL)</span>
                </div>
              )}

              {/* Overlay when no bot selected */}
              <div style={{ position: 'relative' }}>
                <CandleChart
                  data={result.chart_candles}
                  height={440}
                  markers={chartMarkersFormatted}
                />
                {!chartBotFilter && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(15, 23, 42, 0.55)',
                    pointerEvents: 'none',
                  }}>
                    <div style={{
                      background: '#1e293b', border: '1px solid #334155',
                      borderRadius: 10, padding: '12px 24px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>
                        Selecione um bot acima para ver suas operações no gráfico
                      </div>
                      <div style={{ fontSize: 11, color: '#475569' }}>
                        {result.chart_markers?.length || 0} operações disponíveis · {bots.length} bots
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>
                {result.chart_candles.length === result.candles_simulated
                  ? `${result.chart_candles.length} candles completos — zoom e arraste para ver o detalhe de minutos.`
                  : `${result.chart_candles.length} de ${result.candles_simulated} candles (período muito longo). Zoom e arraste para explorar.`
                }
              </div>
            </div>
          )}

          {/* ── Equity curves (SECONDARY) ── */}
          {allTimes.length > 0 && (
            <div style={card}>
              {/* Controls bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 700 }}>
                  Curvas de Equity
                </span>
                <span style={{ fontSize: 11, color: '#475569' }}>(clique nos bots acima para filtrar)</span>

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                  {/* Speed */}
                  <span style={{ fontSize: 11, color: '#64748b' }}>Vel:</span>
                  {[
                    { label: '1×', v: 1 },
                    { label: '5×', v: 5 },
                    { label: '20×', v: 20 },
                    { label: '100×', v: 100 },
                  ].map(s => (
                    <button key={s.v} onClick={() => setReplaySpeed(s.v)} style={{
                      background: replaySpeed === s.v ? '#334155' : '#0f172a',
                      color: replaySpeed === s.v ? '#e2e8f0' : '#64748b',
                      border: '1px solid #334155', borderRadius: 4,
                      padding: '3px 8px', cursor: 'pointer', fontSize: 11,
                    }}>{s.label}</button>
                  ))}

                  {/* Play / Pause / Full */}
                  {!isPlaying ? (
                    <button onClick={startReplay} style={{
                      background: '#1e3a5f', color: '#38bdf8',
                      border: '1px solid #38bdf8', borderRadius: 6,
                      padding: '5px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                    }}>▶ Replay</button>
                  ) : (
                    <button onClick={pauseReplay} style={{
                      background: '#334155', color: '#fbbf24',
                      border: '1px solid #fbbf24', borderRadius: 6,
                      padding: '5px 14px', cursor: 'pointer', fontSize: 13,
                    }}>⏸ Pausar</button>
                  )}
                  {playbackIdx !== null && !isPlaying && (
                    <button onClick={showFull} style={{
                      background: '#0f172a', color: '#64748b',
                      border: '1px solid #334155', borderRadius: 6,
                      padding: '5px 12px', cursor: 'pointer', fontSize: 12,
                    }}>⏭ Ver completo</button>
                  )}
                </div>
              </div>

              {/* Replay status bar */}
              {(isPlaying || playbackIdx !== null) && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                    <span>{result?.period?.from?.slice(0, 10)}</span>
                    {currentReplayTime && (
                      <span style={{ color: '#38bdf8', fontWeight: 700 }}>
                        📅 {new Date(currentReplayTime * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        {' · '}
                        candle {displayEnd} / {maxLen}
                      </span>
                    )}
                    <span>{result?.period?.to?.slice(0, 10)}</span>
                  </div>
                  <div style={{ position: 'relative', height: 4, background: '#1e293b', borderRadius: 2, cursor: 'pointer' }}
                    onClick={e => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pct = (e.clientX - rect.left) / rect.width;
                      const idx = Math.floor(pct * maxLen);
                      setPlaybackIdx(Math.max(0, Math.min(maxLen - 1, idx)));
                      setIsPlaying(false);
                    }}
                  >
                    <div style={{
                      height: '100%', background: '#38bdf8', borderRadius: 2,
                      width: `${(displayEnd / maxLen) * 100}%`,
                      transition: isPlaying ? 'none' : 'width 0.2s',
                    }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 16 }}>
                {/* Chart */}
                <div style={{ flex: 1 }}>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="time" tickFormatter={formatTime} tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} domain={[0, 'auto']} />
                      <Tooltip
                        contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 11 }}
                        labelFormatter={t => new Date(t * 1000).toLocaleDateString('pt-BR')}
                        formatter={(v, name) => {
                          const bot = bots.find(b => b.id === name);
                          return [`$${v?.toLocaleString()}`, bot?.name || name];
                        }}
                      />
                      <ReferenceLine y={capital} stroke="#334155" strokeDasharray="4 4"
                        label={{ value: `$${capital.toLocaleString()}`, fill: '#475569', fontSize: 10 }} />
                      <Legend formatter={(id) => bots.find(b => b.id === id)?.name || id} />
                      {bots
                        .filter(b => visibleBots === null || visibleBots.has(b.id))
                        .map(b => (
                          <Line key={b.id} type="monotone" dataKey={b.id} stroke={b.color}
                            dot={false} strokeWidth={2} isAnimationActive={false} />
                        ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Live trade feed (during replay) */}
                {(isPlaying || (playbackIdx !== null && liveFeed.length > 0)) && (
                  <div style={{ width: 200, flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, fontWeight: 700 }}>
                      Trades ao vivo
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {liveFeed.map((e, i) => (
                        <div key={i} style={{
                          background: '#0f172a', borderRadius: 6, padding: '5px 8px', fontSize: 10,
                          borderLeft: `2px solid ${(e.pnl || 0) >= 0 ? '#34d399' : '#f87171'}`,
                          opacity: 1 - i * 0.1,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{e.botEmoji} {e.botName}</span>
                            <span style={{ color: (e.pnl || 0) >= 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>
                              {(e.pnl || 0) >= 0 ? '+' : ''}${e.pnl?.toFixed(1)}
                            </span>
                          </div>
                          <div style={{ color: '#475569', marginTop: 2 }}>
                            {e.side} · {e.reason}
                          </div>
                        </div>
                      ))}
                      {liveFeed.length === 0 && (
                        <div style={{ color: '#475569', fontSize: 10 }}>aguardando trades...</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Multi-TF combined summary — calendar only */}
          {result.mode === 'multitf' && (
            <div style={{ ...card, borderLeft: `4px solid #38bdf8` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#38bdf8' }}>🌐 MTF Universal — Resultado Combinado</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {result.timeframes?.map((tf, i) => (
                      <span key={tf} style={{ color: TF_COLORS[tf] }}>
                        {tf}{i < result.timeframes.length - 1 ? ' → ' : ''}
                      </span>
                    ))}
                    {' '}· ${result.capital_per_tf?.toFixed(2)} por TF
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 28, fontWeight: 800 }}><PnlBadge value={result.total_pnl_pct} /></div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    ${result.total_capital?.toFixed(2)} → ${result.total_final?.toFixed(2)}
                  </div>
                </div>
              </div>
              {/* Combined calendar across all TF sub-bots */}
              <TradeCalendar
                tradeArrays={bots.map(b => b.recent_trades || [])}
              />
            </div>
          )}

          {/* Cumulative PnL panel for selected bot */}
          {chartBotFilter && (() => {
            const selBot = bots.find(b => b.id === chartBotFilter);
            if (!selBot?.equity_curve?.length) return null;
            const pnlData = selBot.equity_curve.map(p => ({
              time: p.time,
              pnl: round2(p.value - (selBot.initial_capital || capital)),
            }));
            const step = Math.max(1, Math.floor(pnlData.length / 500));
            const sampled = pnlData.filter((_, i) => i % step === 0 || i === pnlData.length - 1);
            return (
              <div style={card}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 12 }}>
                  {selBot.emoji} {selBot.name} · PnL Acumulado
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={sampled} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="time" tickFormatter={t => new Date(t*1000).toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit'})} tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={v => `${v>=0?'+':''}$${v.toFixed(0)}`} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 11 }}
                      formatter={v => [`${v>=0?'+':''}$${v?.toFixed(2)}`, 'PnL Acumulado']}
                      labelFormatter={t => new Date(t * 1000).toLocaleDateString('pt-BR')} />
                    <ReferenceLine y={0} stroke="#334155" strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="pnl" stroke={selBot.color} dot={false} strokeWidth={2} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          })()}

          {/* Bot detail cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {bots.map(b => (
              <div key={b.id} style={{ ...card, borderTop: `3px solid ${b.color}`, marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{b.emoji} {b.name}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{b.interval} · {b.leverage}x</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {b.bankrupt ? (
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#f87171' }}>💀 FALIDO</div>
                    ) : (
                      <div style={{ fontSize: 20, fontWeight: 800 }}><PnlBadge value={b.pnl_pct} /></div>
                    )}
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      ${b.initial_capital?.toLocaleString()} → ${b.final_capital?.toLocaleString()}
                    </div>
                  </div>
                </div>

                {b.description && (
                  <div style={{ fontSize: 10, color: '#475569', marginBottom: 8, fontFamily: 'monospace' }}>
                    {b.description}
                  </div>
                )}
                {b.bankrupt && b.bankrupt_at && (
                  <div style={{ background: '#2d1515', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: '#f87171', marginBottom: 10 }}>
                    Capital zerado em {b.bankrupt_at?.slice(0, 16)} · Operações encerradas
                  </div>
                )}
                {b.diagnosis && (
                  <div style={{ background: '#1c1a08', border: '1px solid #a16207', borderRadius: 6, padding: '7px 10px', fontSize: 11, color: '#fbbf24', marginBottom: 10, lineHeight: 1.6 }}>
                    ⚠ {b.diagnosis}
                    {b.signal_stats && (
                      <div style={{ color: '#94a3b8', marginTop: 4, fontSize: 10 }}>
                        Sinais: {b.signal_stats.buy} buy ({(b.signal_stats.avg_conf_buy * 100)?.toFixed(0)}%) · {b.signal_stats.sell} sell ({(b.signal_stats.avg_conf_sell * 100)?.toFixed(0)}%) · {b.signal_stats.hold} hold · conf mín bot: {(b.min_confidence * 100)?.toFixed(0)}%
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  <StatBox label="Trades" value={b.total_trades} />
                  <StatBox label="Win rate" value={`${b.win_rate}%`} color={b.win_rate >= 50 ? '#34d399' : '#f87171'} />
                  <StatBox label="Max Drawdown" value={`-${b.max_drawdown_pct}%`} color="#f87171" />
                </div>

                {/* Recent trades toggle */}
                {b.recent_trades?.length > 0 && (
                  <>
                    <button
                      onClick={() => setExpandedBot(expandedBot === b.id ? null : b.id)}
                      style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11, padding: 0, marginBottom: 6 }}
                    >
                      {expandedBot === b.id ? '▲ Ocultar trades' : `▼ Ver últimos ${Math.min(b.recent_trades.length, 10)} trades`}
                    </button>
                    {expandedBot === b.id && (
                      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {b.recent_trades.slice(-10).reverse().map((t, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '4px 0', borderBottom: '1px solid #1e293b' }}>
                            <span style={{ color: t.side === 'long' ? '#34d399' : '#f87171' }}>
                              {t.side === 'long' ? '▲' : '▼'} {t.leverage}x
                            </span>
                            <span style={{ color: '#64748b' }}>{t.reason}</span>
                            <span style={{ color: (t.pnl || 0) >= 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>
                              {(t.pnl || 0) >= 0 ? '+' : ''}${t.pnl?.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Calendar heatmap */}
                    {expandedBot === b.id && b.recent_trades?.length > 0 && (
                      <TradeCalendar trades={b.recent_trades} color={b.color} />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Learning cycle tip */}
          {result.training_examples > 0 && !learnFromBacktest.isSuccess && (
            <div style={{ ...card, background: '#0a1f0a', border: '1px solid #4ade80', marginTop: 16 }}>
              <div style={{ color: '#4ade80', fontWeight: 700, marginBottom: 10, fontSize: 14 }}>
                🎓 Ciclo de aprendizado correto
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 2 }}>
                <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontFamily: 'monospace', fontSize: 11 }}>
                  <span style={{ color: '#a78bfa' }}>1. 🔭 Exploração</span> Jan–Jun (Período A) → bot opera com conf ≥ 51%<br />
                  <span style={{ color: '#4ade80' }}>2. 🎓 Aprender</span> com Período A → modelo aprende os padrões<br />
                  <span style={{ color: '#38bdf8' }}>3. 🎯 Normal</span> Jul–Dez (Período B, nunca visto) → mede melhora real<br />
                  <span style={{ color: '#fbbf24' }}>4. Repetir</span> com Período B → Período C → Período D...
                </div>
                <b style={{ color: '#f87171' }}>NÃO</b> teste no mesmo período que aprendeu — isso é memorização, não aprendizado real.<br />
                {result.exploration_mode
                  ? <span style={{ color: '#a78bfa' }}>✓ Você usou Exploração → os {result.training_examples?.toLocaleString()} exemplos têm boa cobertura do período.</span>
                  : <span style={{ color: '#fbbf24' }}>💡 Dica: rode novamente com 🔭 Modo Exploração para cobrir mais do período antes de aprender.</span>}
              </div>
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* Navegação da etapa 3 */}
      {step === 3 && result && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <button
            onClick={() => setStep(1)}
            style={{
              background: '#0f172a', color: '#94a3b8', border: '1px solid #334155',
              borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 12,
            }}
          >
            ← Voltar à configuração
          </button>
          <button
            onClick={() => { runBacktest.mutate(); setStep(2); }}
            disabled={runBacktest.isPending}
            style={{
              background: '#0f172a', color: '#38bdf8', border: '1px solid #38bdf8',
              borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}
          >
            🔄 Refazer execução
          </button>
        </div>
      )}
      {step === 3 && !result && !runBacktest.isPending && (
        <div style={{ ...card, background: '#0f172a', border: '1px dashed #334155', textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏱</div>
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: 8 }}>Nenhum resultado ainda — volte à etapa 1 e rode um backtest</div>
          <button
            onClick={() => setStep(1)}
            style={{
              background: '#1e3a5f', color: '#38bdf8', border: '1px solid #38bdf8',
              borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            ← Ir para Configuração
          </button>
        </div>
      )}
    </div>
  );
}
